export const translations = {
  en: {
    meta: {
      title: "Abraao Nascimento | Senior iOS Developer",
      description: "Portfolio of Abraao Nascimento, Senior iOS Developer focused on Swift, SwiftUI, UIKit, mobile architecture and product quality.",
      ogTitle: "Abraao Nascimento | Senior iOS Developer",
      ogDescription: "Senior iOS Developer building reliable mobile products with Swift, SwiftUI, UIKit and strong engineering practices.",
      twitterTitle: "Abraao Nascimento | Senior iOS Developer",
      twitterDescription: "Portfolio with experience, skills and contact links for Senior iOS Developer Abraao Nascimento."
    },
    nav: {
      home: "Home",
      featured: "Featured",
      experience: "Experience",
      skills: "Skills",
      about: "About",
      goHome: "Go to home section",
      toggleNavigation: "Toggle navigation",
      language: "Language"
    },
    hero: {
      eyebrow: "Available for remote and hybrid teams",
      title: "Senior iOS Developer building reliable mobile products.",
      description: "I design and ship scalable iOS experiences with Swift, UIKit, SwiftUI and strong engineering practices across architecture, testing and delivery.",
      primaryCta: "Let's talk",
      secondaryCta: "Featured work",
      imageAlt: "Illustration representing Abraao as an iOS developer"
    },
    featured: {
      title: "Featured Work",
      intro: "A few product contexts that represent the kind of mobile work I enjoy most: integration-heavy apps, quality-focused delivery and complex product environments.",
      projects: [
        {
          title: "Mobility Platform",
          company: "CEiiA",
          summary: "iOS experience for a mobility product that brings multiple services into a single, more accessible flow.",
          focus: ["Swift implementation", "Service integration", "Usability and accessibility"]
        },
        {
          title: "Paddy Power Native App",
          company: "Blip",
          summary: "Native iOS delivery with strong engineering discipline around architecture, unit tests and UI tests.",
          focus: ["Clean code practices", "Testing strategy", "Software quality"]
        },
        {
          title: "Montepio MBWay Phase 2",
          company: "ITSector",
          summary: "Feature completion and integration work in a legacy banking environment with cross-team coordination.",
          focus: ["Objective-C legacy code", "REST and Core Data", "Delivery under constraints"]
        }
      ]
    },
    experience: {
      title: "Experience",
      intro: "Selected roles across mobility, betting, banking, R&D and e-commerce products.",
      stats: [
        { number: "15+", label: "years building software products" },
        { number: "8", label: "companies across Brazil and Portugal" },
        { number: "iOS", label: "focus on delivery, quality and leadership" }
      ],
      experiences: [
        {
          company: "CEiiA",
          location: "Porto",
          role: "Mobile Software Developer",
          date: "Dec 2023 - Present",
          description: "Building a mobility product in Swift that integrates multiple services into a more accessible mobile experience.",
          highlights: ["Service integration", "Interaction design", "Product accessibility"],
          stack: ["Swift", "iOS", "Service Integration"]
        },
        {
          company: "Blip",
          location: "Porto",
          role: "iOS Engineer",
          date: "Feb 2022 - Jun 2023",
          description: "Worked on the Paddy Power native app with strong emphasis on clean architecture, unit testing and UI testing.",
          highlights: ["Native app delivery", "Unit testing", "UI testing"],
          stack: ["Swift", "XCTest", "Design Patterns"]
        },
        {
          company: "ITSector",
          location: "Porto",
          role: "iOS Engineer",
          date: "Mar 2021 - Sep 2021",
          description: "Led iOS delivery for the second phase of Montepio Bank MBWay, closing integrations and shipping features in a legacy environment.",
          highlights: ["Legacy modernization", "Cross-team coordination", "Feature completion"],
          stack: ["Objective-C", "Storyboard", "Core Data"]
        },
        {
          company: "Eldorado",
          location: "Manaus",
          role: "iOS Tech Leader",
          date: "Nov 2020 - Mar 2021",
          description: "Led the MotoTalk project for Motorola, guiding the team and improving an existing Objective-C codebase.",
          highlights: ["Tech leadership", "Legacy code updates", "Team guidance"],
          stack: ["Objective-C", "Leadership", "iOS"]
        },
        {
          company: "SIDIA",
          location: "Manaus",
          role: "Senior iOS Developer",
          date: "Jan 2019 - Nov 2020",
          description: "Developed iOS interfaces for Samsung service integrations in an R&D environment focused on innovation and delivery quality.",
          highlights: ["Samsung project", "Interface development", "R&D environment"],
          stack: ["Swift", "iOS", "Service Integration"]
        },
        {
          company: "Neemu (Linx)",
          location: "Manaus",
          role: "Senior iOS Developer",
          date: "Sep 2012 - Jan 2019",
          description: "Built and maintained mobile commerce experiences, including the Mobly Store app, and delivered solutions for retail brands like Sephora.",
          highlights: ["E-commerce apps", "Long-term product ownership", "Multi-stack delivery"],
          stack: ["Objective-C", "Swift", "MVP"]
        },
        {
          company: "Communy",
          location: "Manaus",
          role: "Partner and iOS Developer",
          date: "Jan 2016 - Jan 2019",
          description: "Co-founded and built an app to improve communication between residents, trustees and administrators.",
          highlights: ["Co-founder role", "Product development", "Cross-platform adaptation"],
          stack: ["iOS", "Product", "UX Adaptation"]
        },
        {
          company: "Rede Amazônica",
          location: "Manaus",
          role: "Developer",
          date: "Jan 2009 - Sep 2012",
          description: "Started in web development and evolved into mobile engineering, building the foundation that led to my iOS career.",
          highlights: ["Career start", "Web to mobile transition", "Strong fundamentals"],
          stack: ["PHP", "MySQL", "HTML/CSS"]
        }
      ]
    },
    skills: {
      title: "Skills",
      intro: "A focused overview of the technical and collaboration skills I bring to mobile teams — from shipping iOS features and improving architecture to strengthening quality, delivery and teamwork.",
      imageAlt: "Skills illustration",
      strengths: [
        {
          title: "iOS Delivery",
          description: "Production-focused work across Swift, UIKit and SwiftUI with attention to performance, maintainability and user experience."
        },
        {
          title: "Quality Mindset",
          description: "Testing, code review and architecture decisions that keep apps stable, scalable and easier to evolve over time."
        },
        {
          title: "Product Collaboration",
          description: "Comfortable working with design, backend and product teams to turn requirements into reliable mobile experiences."
        }
      ],
      coreStackTitle: "Core Stack",
      coreStackIntro: "Main tools, technologies and engineering practices used across iOS projects.",
      coreStack: [
        { title: "Languages", items: ["Swift", "Objective-C", "JavaScript", "C++", "PHP", "SQL"] },
        { title: "iOS Development", items: ["UIKit", "SwiftUI", "iOS SDK", "Core Data", "Core Animation", "REST APIs", "JSON/XML parsing"] },
        { title: "Frameworks & Patterns", items: ["SPM", "CocoaPods", "RxSwift", "Alamofire", "Firebase", "Combine", "MVVM", "MVP", "MVC"] },
        { title: "Testing & Quality", items: ["XCTest", "Unit Testing", "UI Testing", "TDD", "Code Review", "CI with GitHub Actions"] }
      ],
      deliveryTitle: "Delivery, Leadership & Product",
      deliveryIntro: "The broader skill set that supports shipping solid apps, collaborating well and improving team execution.",
      secondaryImageAlt: "Scrum and collaboration illustration",
      deliverySkills: [
        { title: "Delivery & Release", items: ["App Store Connect", "TestFlight", "Crashlytics", "Release workflows", "Maintenance and support"] },
        { title: "Collaboration", items: ["GitHub", "GitLab", "Git Flow", "Pull Requests", "JIRA", "Trello"] },
        { title: "Leadership & Process", items: ["Agile", "Scrum", "Kanban", "Sprint planning", "Retrospectives", "Mentoring"] },
        { title: "Product & UX", items: ["Accessibility best practices", "User-centered design", "Feature prioritization", "Technical documentation"] }
      ],
      softSkillsTitle: "Soft Skills",
      softSkills: [
        "Problem solving in complex codebases",
        "Clear cross-functional communication",
        "Team leadership and mentoring",
        "Attention to detail and product quality",
        "Adaptability to new tools and domains",
        "Continuous learning mindset"
      ]
    },
    about: {
      title: "About",
      intro: "I'm a mobile software developer focused on building reliable, user-centered iOS products. Over the years I've worked across mobility, e-commerce, banking and R&D contexts, which gave me a strong mix of product sensitivity, technical depth and delivery discipline.",
      copy: "I enjoy improving architecture, strengthening quality practices and collaborating with teams to turn complex requirements into experiences that feel simple for the user. My best work usually happens where product thinking, engineering quality and team alignment meet.",
      imageAlt: "Abraao illustration",
      focusAreas: [
        "Senior iOS development",
        "Architecture and maintainability",
        "Testing and delivery quality",
        "Cross-functional product work"
      ],
      principles: [
        {
          title: "Engineering with care",
          description: "I value clean architecture, readable code and pragmatic decisions that help products evolve without unnecessary complexity."
        },
        {
          title: "Quality as part of delivery",
          description: "Testing, review and maintainability are part of the job — not something added only at the end of a release cycle."
        },
        {
          title: "Collaboration over silos",
          description: "The best mobile products happen when engineering, design and product work closely with shared context and trust."
        }
      ],
      contactEyebrow: "Open to connect",
      contactTitle: "Let's talk about iOS, product quality or mobile teams.",
      contactDescription: "If you want to discuss an opportunity, exchange ideas or just connect, LinkedIn is the fastest way to reach me.",
      contactCta: "Message on LinkedIn",
      openProfileLabelPrefix: "Open profile on",
      links: [
        { title: "GitHub", description: "Code, experiments and repositories" },
        { title: "StackOverflow", description: "Technical answers and community profile" },
        { title: "LinkedIn", description: "Professional profile and contact" },
        { title: "Medium", description: "Writing and ideas about development" }
      ]
    },
    footer: {
      copyrightLabel: "abraaoan"
    }
  },
  pt: {
    meta: {
      title: "Abraao Nascimento | Desenvolvedor iOS Sênior",
      description: "Portfólio de Abraao Nascimento, Desenvolvedor iOS Sênior com foco em Swift, SwiftUI, UIKit, arquitetura mobile e qualidade de produto.",
      ogTitle: "Abraao Nascimento | Desenvolvedor iOS Sênior",
      ogDescription: "Desenvolvedor iOS Sênior criando produtos mobile confiáveis com Swift, SwiftUI, UIKit e práticas sólidas de engenharia.",
      twitterTitle: "Abraao Nascimento | Desenvolvedor iOS Sênior",
      twitterDescription: "Portfólio com experiência, skills e links de contato de Abraao Nascimento, Desenvolvedor iOS Sênior."
    },
    nav: {
      home: "Início",
      featured: "Destaques",
      experience: "Experiência",
      skills: "Skills",
      about: "Sobre",
      goHome: "Ir para a seção inicial",
      toggleNavigation: "Alternar navegação",
      language: "Idioma"
    },
    hero: {
      eyebrow: "Disponível para times remotos e híbridos",
      title: "Desenvolvedor iOS Sênior criando produtos mobile confiáveis.",
      description: "Projeto e entrego experiências iOS escaláveis com Swift, UIKit, SwiftUI e boas práticas de arquitetura, testes e entrega.",
      primaryCta: "Vamos conversar",
      secondaryCta: "Projetos em destaque",
      imageAlt: "Ilustração representando Abraao como desenvolvedor iOS"
    },
    featured: {
      title: "Projetos em Destaque",
      intro: "Alguns contextos de produto que representam bem o tipo de trabalho mobile que mais gosto: apps com integrações complexas, foco em qualidade e ambientes desafiadores.",
      projects: [
        {
          title: "Plataforma de Mobilidade",
          company: "CEiiA",
          summary: "Experiência iOS para um produto de mobilidade que reúne múltiplos serviços em um fluxo único e mais acessível.",
          focus: ["Implementação em Swift", "Integração de serviços", "Usabilidade e acessibilidade"]
        },
        {
          title: "App Nativo Paddy Power",
          company: "Blip",
          summary: "Entrega nativa iOS com forte disciplina de engenharia em arquitetura, testes unitários e testes de interface.",
          focus: ["Práticas de clean code", "Estratégia de testes", "Qualidade de software"]
        },
        {
          title: "Montepio MBWay Fase 2",
          company: "ITSector",
          summary: "Conclusão de features e integrações em um ambiente bancário legado com coordenação entre times.",
          focus: ["Código legado em Objective-C", "REST e Core Data", "Entrega sob restrições"]
        }
      ]
    },
    experience: {
      title: "Experiência",
      intro: "Algumas passagens por produtos de mobilidade, betting, banking, P&D e e-commerce.",
      stats: [
        { number: "15+", label: "anos construindo produtos de software" },
        { number: "8", label: "empresas entre Brasil e Portugal" },
        { number: "iOS", label: "foco em entrega, qualidade e liderança" }
      ],
      experiences: [
        {
          company: "CEiiA",
          location: "Porto",
          role: "Mobile Software Developer",
          date: "Dez 2023 - Atual",
          description: "Desenvolvendo um produto de mobilidade em Swift que integra múltiplos serviços em uma experiência mobile mais acessível.",
          highlights: ["Integração de serviços", "Design de interação", "Acessibilidade do produto"],
          stack: ["Swift", "iOS", "Integração de Serviços"]
        },
        {
          company: "Blip",
          location: "Porto",
          role: "iOS Engineer",
          date: "Fev 2022 - Jun 2023",
          description: "Atuei no app nativo da Paddy Power com forte foco em arquitetura limpa, testes unitários e testes de interface.",
          highlights: ["Entrega de app nativo", "Testes unitários", "Testes de interface"],
          stack: ["Swift", "XCTest", "Padrões de Projeto"]
        },
        {
          company: "ITSector",
          location: "Porto",
          role: "iOS Engineer",
          date: "Mar 2021 - Set 2021",
          description: "Liderei a entrega iOS da segunda fase do Montepio MBWay, concluindo integrações e entregando features em um ambiente legado.",
          highlights: ["Modernização de legado", "Coordenação entre times", "Conclusão de features"],
          stack: ["Objective-C", "Storyboard", "Core Data"]
        },
        {
          company: "Eldorado",
          location: "Manaus",
          role: "Líder Técnico iOS",
          date: "Nov 2020 - Mar 2021",
          description: "Liderei o projeto MotoTalk para a Motorola, apoiando o time e evoluindo uma base existente em Objective-C.",
          highlights: ["Liderança técnica", "Atualização de código legado", "Apoio ao time"],
          stack: ["Objective-C", "Liderança", "iOS"]
        },
        {
          company: "SIDIA",
          location: "Manaus",
          role: "Desenvolvedor iOS Sênior",
          date: "Jan 2019 - Nov 2020",
          description: "Desenvolvi interfaces iOS para integrações de serviços da Samsung em um ambiente de P&D com foco em inovação e qualidade de entrega.",
          highlights: ["Projeto Samsung", "Desenvolvimento de interfaces", "Ambiente de P&D"],
          stack: ["Swift", "iOS", "Integração de Serviços"]
        },
        {
          company: "Neemu (Linx)",
          location: "Manaus",
          role: "Desenvolvedor iOS Sênior",
          date: "Set 2012 - Jan 2019",
          description: "Construí e mantive experiências mobile de e-commerce, incluindo o app da Mobly Store, além de soluções para marcas como Sephora.",
          highlights: ["Apps de e-commerce", "Ownership de produto", "Entrega multi-stack"],
          stack: ["Objective-C", "Swift", "MVP"]
        },
        {
          company: "Communy",
          location: "Manaus",
          role: "Sócio e Desenvolvedor iOS",
          date: "Jan 2016 - Jan 2019",
          description: "Co-fundei e desenvolvi um app para melhorar a comunicação entre moradores, síndicos e administradoras.",
          highlights: ["Papel de cofundador", "Desenvolvimento de produto", "Adaptação cross-platform"],
          stack: ["iOS", "Produto", "Adaptação de UX"]
        },
        {
          company: "Rede Amazônica",
          location: "Manaus",
          role: "Desenvolvedor",
          date: "Jan 2009 - Set 2012",
          description: "Comecei no desenvolvimento web e evoluí para engenharia mobile, construindo a base que levou à minha carreira em iOS.",
          highlights: ["Início de carreira", "Transição web para mobile", "Base técnica sólida"],
          stack: ["PHP", "MySQL", "HTML/CSS"]
        }
      ]
    },
    skills: {
      title: "Skills",
      intro: "Uma visão objetiva das competências técnicas e de colaboração que levo para times mobile — desde entrega de features iOS e evolução de arquitetura até qualidade, delivery e trabalho em equipe.",
      imageAlt: "Ilustração de skills",
      strengths: [
        {
          title: "Entrega iOS",
          description: "Atuação focada em produção com Swift, UIKit e SwiftUI, com atenção a performance, manutenção e experiência do usuário."
        },
        {
          title: "Mentalidade de Qualidade",
          description: "Testes, code review e decisões de arquitetura que mantêm os apps estáveis, escaláveis e mais fáceis de evoluir."
        },
        {
          title: "Colaboração com Produto",
          description: "Conforto para trabalhar com design, backend e produto, transformando requisitos em experiências mobile confiáveis."
        }
      ],
      coreStackTitle: "Stack Principal",
      coreStackIntro: "Principais ferramentas, tecnologias e práticas de engenharia usadas nos projetos iOS.",
      coreStack: [
        { title: "Linguagens", items: ["Swift", "Objective-C", "JavaScript", "C++", "PHP", "SQL"] },
        { title: "Desenvolvimento iOS", items: ["UIKit", "SwiftUI", "iOS SDK", "Core Data", "Core Animation", "REST APIs", "JSON/XML parsing"] },
        { title: "Frameworks & Padrões", items: ["SPM", "CocoaPods", "RxSwift", "Alamofire", "Firebase", "Combine", "MVVM", "MVP", "MVC"] },
        { title: "Testes & Qualidade", items: ["XCTest", "Unit Testing", "UI Testing", "TDD", "Code Review", "CI com GitHub Actions"] }
      ],
      deliveryTitle: "Entrega, Liderança & Produto",
      deliveryIntro: "O conjunto mais amplo de habilidades que apoia a entrega de apps sólidos, boa colaboração e evolução da execução do time.",
      secondaryImageAlt: "Ilustração de scrum e colaboração",
      deliverySkills: [
        { title: "Entrega & Release", items: ["App Store Connect", "TestFlight", "Crashlytics", "Fluxos de release", "Manutenção e suporte"] },
        { title: "Colaboração", items: ["GitHub", "GitLab", "Git Flow", "Pull Requests", "JIRA", "Trello"] },
        { title: "Liderança & Processo", items: ["Agile", "Scrum", "Kanban", "Sprint planning", "Retrospectives", "Mentoria"] },
        { title: "Produto & UX", items: ["Boas práticas de acessibilidade", "Design centrado no usuário", "Priorização de features", "Documentação técnica"] }
      ],
      softSkillsTitle: "Soft Skills",
      softSkills: [
        "Resolução de problemas em codebases complexos",
        "Comunicação clara entre áreas",
        "Liderança e mentoria de time",
        "Atenção a detalhes e qualidade de produto",
        "Adaptabilidade a novas ferramentas e contextos",
        "Mentalidade de aprendizado contínuo"
      ]
    },
    about: {
      title: "Sobre",
      intro: "Sou um desenvolvedor de software mobile focado em construir produtos iOS confiáveis e centrados no usuário. Ao longo dos anos, atuei em contextos de mobilidade, e-commerce, banking e P&D, o que me deu uma combinação forte de visão de produto, profundidade técnica e disciplina de entrega.",
      copy: "Gosto de evoluir arquitetura, fortalecer práticas de qualidade e colaborar com times para transformar requisitos complexos em experiências simples para o usuário. Meu melhor trabalho normalmente acontece onde pensamento de produto, qualidade de engenharia e alinhamento de time se encontram.",
      imageAlt: "Ilustração de Abraao",
      focusAreas: [
        "Desenvolvimento iOS sênior",
        "Arquitetura e manutenção",
        "Testes e qualidade de entrega",
        "Trabalho cross-functional com produto"
      ],
      principles: [
        {
          title: "Engenharia com cuidado",
          description: "Valorizo arquitetura limpa, código legível e decisões pragmáticas que ajudam o produto a evoluir sem complexidade desnecessária."
        },
        {
          title: "Qualidade como parte da entrega",
          description: "Testes, revisão e manutenção fazem parte do trabalho — não algo adicionado só no final do ciclo de release."
        },
        {
          title: "Colaboração acima de silos",
          description: "Os melhores produtos mobile nascem quando engenharia, design e produto trabalham próximos, com contexto compartilhado e confiança."
        }
      ],
      contactEyebrow: "Aberto para contato",
      contactTitle: "Vamos falar sobre iOS, qualidade de produto ou times mobile.",
      contactDescription: "Se quiser conversar sobre uma oportunidade, trocar ideias ou só conectar, o LinkedIn é a forma mais rápida de me encontrar.",
      contactCta: "Enviar mensagem no LinkedIn",
      openProfileLabelPrefix: "Abrir perfil no",
      links: [
        { title: "GitHub", description: "Código, experimentos e repositórios" },
        { title: "StackOverflow", description: "Respostas técnicas e perfil na comunidade" },
        { title: "LinkedIn", description: "Perfil profissional e contato" },
        { title: "Medium", description: "Textos e ideias sobre desenvolvimento" }
      ]
    },
    footer: {
      copyrightLabel: "abraaoan"
    }
  }
};

export const supportedLanguages = ["en", "pt"];
